/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  CloseButton,
  Flex,
  FormControl,
  Heading,
  Text,
} from "@chakra-ui/react";
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import { Field, Formik } from "formik";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { AuthInput } from "components/auth/AuthInput";

import { initialValues, validationSchema, SignUpFormInputs } from "./constants";
import { useSignUpLogic } from "./hooks/useSignUpLogic";
import { useSignUpStyles } from "./hooks/useSignUpStyles";
import { PasswordErrors } from "./components/PasswordErrors";
import { GoogleSignUpButton } from "./components/GoogleSignUpButton";
import { FormFooter } from "./components/FormFooter";
import { ErrorAlert } from "../signIn/components/ErrorAlert";

export default function SignUp() {
  const {
    authStyles,
    setPassword,
    setPasswordAgain,
    passwordErrors,
    isPasswordValid,
    isFetchingProfile,
    isRegistering,
    isSucceed,
    setIsSucceed,
    showError,
    authErrorMessageText,
    goToSignIn,
    handleGoogleSignUp,
    handleRegister,
  } = useSignUpLogic();

  const styles = useSignUpStyles(authStyles);

  // Prefill the email field from a ?email=... query param. Used by the
  // "Create your account" CTA in customer status emails so guest checkout
  // customers don't have to retype their email.
  const [searchParams] = useSearchParams();
  const prefilledEmail = searchParams.get("email") ?? "";
  const formInitialValues = useMemo(
    () => ({ ...initialValues, email: prefilledEmail }),
    [prefilledEmail]
  );

  return (
    <DefaultAuth>
      <Flex
        {...authStyles.container}
        fontFamily="General Sans, Inter, sans-serif"
        sx={{
          form: {
            maxWidth: "453px",
            width: "100%",
          },
        }}
      >
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={async (values, { validateForm }) => {
            const errors = await validateForm();
            if (Object.keys(errors).length > 0) {
              return;
            }

            if (!isPasswordValid) {
              return;
            }

            await handleRegister(values as SignUpFormInputs);
          }}
        >
          {({ handleSubmit, errors, submitCount }) => (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <Flex
                zIndex="2"
                direction="column"
                padding={authStyles.padding?.desktop?.form}
                w="100%"
                bg="#F5F7FA"
                borderRadius="12px"
                boxShadow="none"
              >
                {isSucceed && (
                  <Alert status="success" mb="20px" bgColor="#0A0">
                    <AlertIcon color="#FFF" />
                    <Box>
                      <AlertTitle
                        color="#FFF"
                        fontSize="15px"
                        lineHeight="100%"
                      >
                        New Account has created successfully.
                      </AlertTitle>
                      <AlertDescription
                        display="block"
                        mt={2}
                        color="#FFF"
                        fontSize="14px"
                        lineHeight="120%"
                      >
                        Welcome to Languex Translation Platfrom!
                      </AlertDescription>
                    </Box>
                    <CloseButton
                      ml="auto"
                      position="relative"
                      color="#FFF"
                      onClick={() => setIsSucceed(false)}
                    />
                  </Alert>
                )}

                <Heading
                  color={styles.textColor}
                  {...authStyles.heading}
                  mb={"24px"}
                >
                  Create an account
                </Heading>

                {showError && authErrorMessageText && (
                  <ErrorAlert message={authErrorMessageText} />
                )}

                <GoogleSignUpButton
                  authStyles={authStyles}
                  styles={styles}
                  isLoading={isFetchingProfile || isRegistering}
                  onClick={() => handleGoogleSignUp()}
                />

                <Flex align="center" my="20px">
                  <HSeparator />
                  <Text {...authStyles.separator}>OR</Text>
                  <HSeparator />
                </Flex>

                <Flex
                  gap={{ base: "0px", md: "12px" }}
                  w="full"
                  direction={{ base: "column", md: "row" }}
                >
                  <FormControl
                    isInvalid={submitCount > 0 && !!errors.first_name}
                    mb="12px"
                  >
                    <Field name="first_name">
                      {({ field }: any) => (
                        <AuthInput
                          {...field}
                          role="customer"
                          chakraVariant="authCustomerNew"
                          type="text"
                          id="first_name"
                          placeholder="First name"
                          isInvalid={submitCount > 0 && !!errors.first_name}
                        />
                      )}
                    </Field>
                  </FormControl>

                  <FormControl
                    isInvalid={submitCount > 0 && !!errors.last_name}
                    mb="12px"
                  >
                    <Field name="last_name">
                      {({ field }: any) => (
                        <AuthInput
                          {...field}
                          role="customer"
                          chakraVariant="authCustomerNew"
                          type="text"
                          id="last_name"
                          placeholder="Last name"
                          isInvalid={submitCount > 0 && !!errors.last_name}
                        />
                      )}
                    </Field>
                  </FormControl>
                </Flex>

                <FormControl
                  isInvalid={submitCount > 0 && !!errors.email}
                  mb="12px"
                >
                  <Field name="email">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="email"
                        id="email"
                        placeholder="Email Address"
                        isInvalid={submitCount > 0 && !!errors.email}
                      />
                    )}
                  </Field>
                </FormControl>

                <FormControl
                  isInvalid={submitCount > 0 && !!errors.password}
                  mb="12px"
                >
                  <Field name="password">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="password"
                        id="password"
                        placeholder="Password"
                        title="Password must be at least 8 characters and include one number."
                        isInvalid={
                          submitCount > 0 &&
                          (!!errors.password || passwordErrors.length > 0)
                        }
                        onChange={(e: any) => {
                          field.onChange(e);
                          setPassword(e.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <Text fontSize="12px" color="#000000" mt="4px">
                    Password must be at least 8 characters and include one
                    number.
                  </Text>
                </FormControl>

                <FormControl
                  isInvalid={
                    submitCount > 0 &&
                    (!!errors.password_again || passwordErrors.length > 0)
                  }
                  mb="12px"
                >
                  <Field name="password_again">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        type="password"
                        id="password_again"
                        placeholder="Repeat password"
                        title="Password must be at least 8 characters and include one number."
                        isInvalid={
                          submitCount > 0 &&
                          (!!errors.password_again || passwordErrors.length > 0)
                        }
                        onChange={(e: any) => {
                          field.onChange(e);
                          setPasswordAgain(e.target.value);
                        }}
                      />
                    )}
                  </Field>
                </FormControl>

                {submitCount > 0 && passwordErrors.length > 0 && (
                  <PasswordErrors errors={passwordErrors} />
                )}

                <FormFooter
                  authStyles={authStyles}
                  isLoading={isRegistering}
                  onSignIn={goToSignIn}
                />
              </Flex>
            </form>
          )}
        </Formik>
      </Flex>
    </DefaultAuth>
  );
}
